import React, { useRef, useState, useCallback } from 'react';
import { Checkbox } from 'primereact/checkbox';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { Button } from 'primereact/button';
import { getSubtypeHeader } from '../../../utils/subTypeHeader';
import { Divider } from 'primereact/divider';
import { Toast } from 'primereact/toast';
import { deleteImageFromStorage } from '../../../hooks/useBusinessConfig';


export default function PaymentMethodForm({ shopType, formState, setFormState, paymentMethods: paymentMethodsObj }) {
  const toast = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const subtype = shopType;
  const headerText = getSubtypeHeader(subtype);
  const fileInputRefs = useRef({});

  const handleCheckboxChange = (method, checked) => {
    const newMethods = checked
      ? [...(formState[`paymentMethods_${subtype}`] || []), method]
      : (formState[`paymentMethods_${subtype}`] || []).filter(m => m !== method);
    setFormState(`paymentMethods_${subtype}`, newMethods);
  };

  const showError = (message) => {
    toast.current?.show({
      severity: 'error',
      summary: 'Error',
      detail: message,
      life: 5000
    });
  };

  const showSuccess = (message) => {
    toast.current?.show({
      severity: 'success',
      summary: 'Success',
      detail: message,
      life: 3000
    });
  };

  const handleUrlChange = (method, value) => {
    setFormState(`paymentUrls_${subtype}`, {
      ...(formState[`paymentUrls_${subtype}`] || {}),
      [method]: value
    });
  };

  const handleImageUpload = (method, event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setFormState(`paymentImages_${subtype}`, {
      ...(formState[`paymentImages_${subtype}`] || {}),
      [method]: {
        file,
        name: file.name,
        type: file.type,
        isNew: true
      }
    });

    // Reset the file input
    if (fileInputRefs.current[method]) {
      fileInputRefs.current[method].value = '';
    }
  };

  const removeImage = async (method, e) => {
    e.stopPropagation();
    
    // Get the current image data before removing it
    const currentImage = formState[`paymentImages_${subtype}`]?.[method];
    
    // Remove from form state
    setFormState(`paymentImages_${subtype}`, {
      ...(formState[`paymentImages_${subtype}`] || {}),
      [method]: null
    });
    
    // If this was an existing image with a path, delete it from storage
    if (currentImage?.path) {
      try {
        await deleteImageFromStorage(currentImage.path);
      } catch (error) {
        console.error('Error removing image:', error);
        // Optionally show error to user
        showError('Failed to delete image. Please try again.');
      }
    }
  };

  const triggerFileInput = (method) => {
    if (fileInputRefs.current[method]) {
      fileInputRefs.current[method].click();
    }
  };

  return (
    <div className="form-section p-4 bg-white rounded-lg shadow-sm">
      <Toast ref={toast} position="top-right" />

      <h3 className="section-title text-lg font-semibold mb-4">Payment Methods</h3>
      <div className="space-y-3">
        {Object.entries(paymentMethodsObj[0] || {}).map(([value, method]) => {
          const isChecked = formState[`paymentMethods_${subtype}`]?.includes(value);
          const showInputs = isChecked && method.requiresInput;

          return (
            <div key={value} className="space-y-2 py-2">
              <div className="flex items-center">
                <Checkbox
                  inputId={`payment-${value}-${subtype}`}
                  checked={isChecked}
                  onChange={(e) => handleCheckboxChange(value, e.checked)}
                  className="mr-2"
                />
                <label
                  htmlFor={`payment-${value}-${subtype}`}
                  className="text-gray-700 font-medium cursor-pointer"
                >
                  {method.label}
                </label>
              </div>

              {showInputs && (
                <div className="ml-6 space-y-2 pb-2">
                  {formState[`paymentImages_${subtype}`]?.[value] ? (
                    <div className="flex items-center justify-between bg-gray-50 p-2 rounded-md border">
                      <span className="text-sm text-gray-700">
                        {formState[`paymentImages_${subtype}`]?.[value]?.name || 'QR Code'}
                      </span>
                      <Button
                        type="button"
                        onClick={(e) => removeImage(value, e)}
                        className="p-button-text p-1"
                        title="Remove QR code"
                        icon="pi pi-times"
                      />
                    </div>
                  ) : (
                    <div className="flex gap-2 align-items-center items-start">
                      <div className="flex-1">
                        <div className="p-inputgroup flex-1">
                          <span className="p-inputgroup-addon">
                            <i className={`pi ${method.icon || 'pi-link'}`}></i>
                          </span>
                          <InputText
                            value={formState[`paymentUrls_${subtype}`]?.[value] || ''}
                            onChange={(e) => handleUrlChange(value, e.target.value)}
                            placeholder={method.placeholder || 'Payment link'}
                            className="w-full text-sm"
                          />
                        </div>
                      </div>
                      <Divider layout="vertical" />
                      <div className="flex-shrink-0">
                        <input
                          type="file"
                          ref={el => fileInputRefs.current[value] = el}
                          onChange={(e) => handleImageUpload(value, e)}
                          id={`qr-upload-${value}`}
                          className="hidden"
                        />
                        <div className="flex items-center align-items-center">
                          <label
                            htmlFor={`qr-upload-${value}`}
                            className={`inline-flex items-center justify-center gap-1 px-3 h-10 border rounded-md cursor-pointer hover:bg-gray-50 transition-colors whitespace-nowrap ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            title="Upload QR Code"
                            disabled={isUploading}
                          >
                            {isUploading ? (
                              <>
                                <i className="pi pi-spin pi-spinner text-gray-500"></i>
                                <span className="text-xs ml-2">Uploading...</span>
                              </>
                            ) : (
                              <>
                                <i className="pi pi-qrcode text-gray-500"></i>
                                <span className="text-xs">Upload QR Code</span>
                              </>
                            )}
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {formState[`paymentMethods_${subtype}`]?.length > 0 && (
        <div className="mt-6 space-y-4">
          {formState[`paymentMethods_${subtype}`]?.some(method =>
            ['bank', 'credit_card'].includes(method)
          ) && (
              <Message
                severity="info"
                className="p-3"
                content={(
                  <div className="space-y-1 text-sm">
                    <p className="font-semibold text-base">🔒 Secure Payment Information</p>
                    <p>• Only provide payment links (e.g., bank transfer links, payment gateway URLs)</p>
                    <p>• Never share sensitive banking credentials like account numbers or card details</p>
                    <p>• For bank transfers, use your bank's official payment request link</p>
                    <p>• For credit cards, use secure payment gateway links only</p>
                  </div>
                )}
              />
            )}

          <Message
            severity="info"
            className="p-3"
            content={
              <div className="text-sm">
                <p className="font-semibold text-base">💡 Tip</p>
                <p>Make sure to test your payment links before saving. Customers will use these to complete their purchases.</p>
              </div>
            }
          />
        </div>
      )}
    </div>
  );
}
